import { useState, useEffect } from 'react';
import SchoolShell from '../../components/school/SchoolShell';
import { useToast } from '../../context/ToastContext';
import api from '../../utils/api';

const SchoolStudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { success, error } = useToast();

  useEffect(() => { loadStudents(); }, []);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const res = await api.get('/schools/students');
      setStudents(res.data.data || []);
    } catch { setStudents([]); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    try {
      await api.post('/schools/students/' + id + '/approve');
      success('Student approved!');
      loadStudents();
    } catch { error('Failed to approve student.'); }
  };

  const handleBlock = async (id) => {
    try {
      await api.post('/schools/students/' + id + '/block');
      success('Student blocked.');
      loadStudents();
    } catch { error('Failed.'); }
  };

  const filtered = students.filter(s => {
    const matchFilter = filter === 'all' || s.status === filter;
    const matchSearch = search === '' || s.name?.toLowerCase().includes(search.toLowerCase()) || s.email?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const statusStyle = (status) => {
    if (status === 'active') return { bg: '#E6F4EC', color: '#1B7F3E', label: 'Active' };
    if (status === 'pending') return { bg: '#FEF3C7', color: '#B45309', label: 'Pending' };
    if (status === 'blocked') return { bg: '#FEE2E2', color: '#DC2626', label: 'Blocked' };
    return { bg: '#F7F8FC', color: '#64748B', label: status };
  };

  return (
    <SchoolShell title="Students" subtitle={students.length + ' total students'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
            style={{ flex: 1, minWidth: '200px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none' }} />
          {['all', 'active', 'pending', 'blocked'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, border: filter === f ? 'none' : '1px solid #E2E8F0', background: filter === f ? '#1A3C6E' : 'white', color: filter === f ? 'white' : '#64748B', cursor: 'pointer', textTransform: 'capitalize' }}>
              {f} {f !== 'all' && '(' + students.filter(s => s.status === f).length + ')'}
            </button>
          ))}
          <button onClick={() => window.location.href='/school/add-student'}
            style={{ padding: '7px 14px', borderRadius: '8px', background: '#FF6B00', color: 'white', fontSize: '12px', fontWeight: 800, border: 'none', cursor: 'pointer' }}>
            + Add Student
          </button>
        </div>

        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F7F8FC', borderBottom: '1px solid #E2E8F0' }}>
                {['Student Name', 'Email', 'Class', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>No students found</td></tr>
              ) : filtered.map((s, i) => {
                const st = statusStyle(s.status);
                return (
                  <tr key={s.id} style={{ borderBottom: i < filtered.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#E8F0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, color: '#1A3C6E' }}>
                          {s.name?.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '12px', color: '#64748B' }}>{s.email}</td>
                    <td style={{ padding: '12px 14px', fontSize: '12px', fontWeight: 700 }}>Class {s.class_id}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: st.bg, color: st.color }}>{st.label}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {s.status === 'pending' && (
                          <button onClick={() => handleApprove(s.id)} style={{ padding: '4px 10px', borderRadius: '6px', background: '#E6F4EC', color: '#1B7F3E', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>✓ Approve</button>
                        )}
                        {s.status === 'active' && (
                          <button onClick={() => handleBlock(s.id)} style={{ padding: '4px 10px', borderRadius: '6px', background: '#FEE2E2', color: '#DC2626', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>Block</button>
                        )}
                        {s.status === 'blocked' && (
                          <button onClick={() => handleApprove(s.id)} style={{ padding: '4px 10px', borderRadius: '6px', background: '#E6F4EC', color: '#1B7F3E', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>Unblock</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </SchoolShell>
  );
};

export default SchoolStudentsPage;
